import { EnvelopeSchema, ErrorCodes, type Envelope } from '@vp/shared-types';

/**
 * Typed request/response channel over postMessage (§8.2): envelope validation,
 * request ids, timeouts, cancellation and error mapping. Used on both sides.
 */
export interface PortLike {
  postMessage(msg: unknown, transfer?: Transferable[]): void;
  onmessage: ((e: MessageEvent) => void) | null;
}

export class RpcError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

export interface CallOptions {
  timeoutMs?: number;
  signal?: AbortSignal;
  transfer?: Transferable[];
}

type Handler = (params: unknown, ctx: { signal: AbortSignal; id: string }) => Promise<unknown> | unknown;

export interface RpcHooks {
  /** A message failed envelope validation or referenced an unknown request. */
  onInvalid?: (reason: string, raw: unknown) => void;
  onEvent?: (method: string, params: unknown) => void;
  /** Transferables to send with a result (e.g. ImageBitmap). */
  transferOf?: (result: unknown) => Transferable[];
}

let seq = 0;

export class RpcEndpoint {
  private pending = new Map<string, { resolve: (v: unknown) => void; reject: (e: Error) => void; timer: ReturnType<typeof setTimeout> | null }>();
  private handlers = new Map<string, Handler>();
  private inflight = new Map<string, AbortController>();
  private closed = false;
  private readonly prefix: string;

  constructor(
    private readonly port: PortLike,
    private readonly hooks: RpcHooks = {},
    prefix = 'r',
  ) {
    this.prefix = prefix + Math.random().toString(36).slice(2, 6);
    port.onmessage = (e) => this.receive(e.data);
  }

  handle(method: string, fn: Handler): void {
    this.handlers.set(method, fn);
  }

  get pendingCount(): number {
    return this.pending.size;
  }

  get inflightCount(): number {
    return this.inflight.size;
  }

  call<T = unknown>(method: string, params: unknown, opts: CallOptions = {}): Promise<T> {
    if (this.closed) return Promise.reject(new RpcError(ErrorCodes.cancelled, 'channel closed'));
    const id = `${this.prefix}${++seq}`;
    return new Promise<T>((resolve, reject) => {
      const timer = opts.timeoutMs
        ? setTimeout(() => {
            this.pending.delete(id);
            this.post({ v: 1, id, kind: 'cancel' });
            reject(new RpcError(ErrorCodes.timeout, `${method} timed out after ${opts.timeoutMs} ms`));
          }, opts.timeoutMs)
        : null;
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject, timer });
      if (opts.signal) {
        if (opts.signal.aborted) {
          this.settle(id, new RpcError(ErrorCodes.cancelled, 'cancelled'));
          return;
        }
        opts.signal.addEventListener(
          'abort',
          () => {
            if (!this.pending.has(id)) return;
            this.post({ v: 1, id, kind: 'cancel' });
            this.settle(id, new RpcError(ErrorCodes.cancelled, 'cancelled'));
          },
          { once: true },
        );
      }
      this.post({ v: 1, id, kind: 'req', method, params }, opts.transfer);
    });
  }

  emit(method: string, params: unknown): void {
    this.post({ v: 1, id: `${this.prefix}e${++seq}`, kind: 'evt', method, params });
  }

  private post(msg: Envelope, transfer?: Transferable[]): void {
    if (this.closed) return;
    try {
      this.port.postMessage(msg, transfer ?? []);
    } catch (e) {
      // non-cloneable payload: report as an error for requests we answer
      if (msg.kind === 'res') this.port.postMessage({ v: 1, id: msg.id, kind: 'err', error: { code: ErrorCodes.internal, message: String(e) } });
    }
  }

  private settle(id: string, err: Error | null, value?: unknown): void {
    const p = this.pending.get(id);
    if (!p) return;
    this.pending.delete(id);
    if (p.timer) clearTimeout(p.timer);
    if (err) p.reject(err);
    else p.resolve(value);
  }

  private receive(raw: unknown): void {
    if (this.closed) return;
    const parsed = EnvelopeSchema.safeParse(raw);
    if (!parsed.success) {
      this.hooks.onInvalid?.('invalid envelope', raw);
      return;
    }
    const m = parsed.data;
    switch (m.kind) {
      case 'res':
        if (!this.pending.has(m.id)) return; // late answer after timeout/cancel
        this.settle(m.id, null, m.result);
        return;
      case 'err':
        if (!this.pending.has(m.id)) return;
        this.settle(m.id, new RpcError(m.error.code, m.error.message));
        return;
      case 'evt':
        this.hooks.onEvent?.(m.method, m.params);
        return;
      case 'cancel':
        this.inflight.get(m.id)?.abort();
        return;
      case 'req':
        void this.serve(m.id, m.method, m.params);
    }
  }

  private async serve(id: string, method: string, params: unknown): Promise<void> {
    const h = this.handlers.get(method);
    if (!h) {
      this.hooks.onInvalid?.(`unknown method ${method}`, { method });
      this.post({ v: 1, id, kind: 'err', error: { code: ErrorCodes.unknownMethod, message: `unknown method ${method}` } });
      return;
    }
    const ac = new AbortController();
    this.inflight.set(id, ac);
    try {
      const result = await h(params, { signal: ac.signal, id });
      if (ac.signal.aborted) return;
      this.post({ v: 1, id, kind: 'res', result }, this.hooks.transferOf?.(result));
    } catch (e) {
      if (ac.signal.aborted) return;
      const code = e instanceof RpcError ? e.code : (e as { code?: string })?.code && typeof (e as { code?: string }).code === 'string' ? (e as { code: string }).code : ErrorCodes.moduleError;
      const message = e instanceof Error ? e.message : String(e);
      this.post({ v: 1, id, kind: 'err', error: { code: code.slice(0, 64), message: message.slice(0, 2000) } });
    } finally {
      this.inflight.delete(id);
    }
  }

  close(): void {
    if (this.closed) return;
    for (const id of [...this.pending.keys()]) this.settle(id, new RpcError(ErrorCodes.cancelled, 'channel closed'));
    for (const ac of this.inflight.values()) ac.abort();
    this.closed = true;
    this.port.onmessage = null;
  }
}
